/**
 * Homly — SidebarDesktop (Phase 1 refonte UX)
 * ===========================================
 * Sidebar 5 sections avec sous-menus collapsibles, palette Dark Luxury.
 * Une section unique reste ouverte à la fois (accordéon).
 * - L'item principal sans children est un lien direct.
 * - L'item avec children expose un caret + dropdown.
 * - L'item enfant actif fait remonter visuellement le parent (active state).
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Lock } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { HomlyLogo } from '../brand/HomlyLogo';
import CockpitScoreCard from '../dashboard/CockpitScoreCard';

function SidebarDesktop({ navItems, user, basePath, mandateCount, unreadMessages }) {
    const location = useLocation();

    // Identifie la section dont un enfant correspond à l'URL active.
    const activeSectionKey = useMemo(() => {
        const path = location.pathname;
        for (const item of navItems) {
            if (item.children) {
                const hit = item.children.some((c) => path === c.to || path.startsWith(`${c.to}/`));
                if (hit) return item.key;
            }
        }
        return null;
    }, [location.pathname, navItems]);

    const [openKey, setOpenKey] = useState(activeSectionKey);

    useEffect(() => {
        // Si l'URL change vers une section différente, on suit.
        if (activeSectionKey) setOpenKey(activeSectionKey);
    }, [activeSectionKey]);

    return (
        <aside
            data-testid="sidebar-desktop"
            className="fixed left-0 top-0 bottom-0 w-64 hidden md:flex flex-col z-50"
            style={{
                background: 'var(--homly-gradient-sidebar)',
                borderRight: '1px solid var(--homly-border-subtle)',
                paddingTop: 'env(safe-area-inset-top, 0px)',
                fontFamily: 'var(--homly-font-body)',
            }}
        >
            {/* Logo */}
            <div
                style={{
                    padding: 'var(--homly-space-6) var(--homly-space-5)',
                    borderBottom: '1px solid var(--homly-border-subtle)',
                }}
            >
                <Link to="/" data-testid="sidebar-logo-link">
                    <HomlyLogo size="lg" />
                </Link>
                <p
                    style={{
                        margin: '4px 0 0 0',
                        fontSize: 10,
                        letterSpacing: '0.16em',
                        textTransform: 'uppercase',
                        color: 'var(--homly-text-tertiary)',
                        fontFamily: 'var(--homly-font-display)',
                        fontWeight: 700,
                    }}
                >
                    Cockpit IA · {user?.role === 'admin' ? 'Manager' : user?.role === 'agent' ? 'Agent' : 'Vendeur'}
                </p>
            </div>

            {/* Cockpit Score — visible uniquement pour admin/agent */}
            {(user?.role === 'admin' || user?.role === 'agent') && (
                <CockpitScoreCard basePath={basePath} />
            )}

            {/* Navigation */}
            <nav
                className="flex-1 overflow-y-auto"
                style={{ padding: 'var(--homly-space-4) var(--homly-space-3)' }}
                aria-label="Navigation principale"
            >
                {navItems.map((item) => {
                    if (item.children) {
                        return (
                            <SectionGroup
                                key={item.key || item.label}
                                section={item}
                                open={openKey === item.key}
                                onToggle={() => setOpenKey(openKey === item.key ? null : item.key)}
                                unreadMessages={unreadMessages}
                                mandateCount={mandateCount}
                            />
                        );
                    }
                    return (
                        <SectionLink
                            key={item.to || item.label}
                            item={item}
                            basePath={basePath}
                            mandateCount={mandateCount}
                            unreadMessages={unreadMessages}
                        />
                    );
                })}
            </nav>

            {/* User card */}
            <div
                style={{
                    padding: 'var(--homly-space-4)',
                    borderTop: '1px solid var(--homly-border-subtle)',
                }}
            >
                <NavLink
                    to={
                        user?.role === 'admin'
                            ? '/admin/profile'
                            : user?.role === 'agent'
                                ? '/agent/profile'
                                : '/seller'
                    }
                    data-testid="sidebar-user-card"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        background: 'var(--homly-bg-elevated)',
                        border: '1px solid var(--homly-border)',
                        borderRadius: 'var(--homly-radius-md)',
                        textDecoration: 'none',
                        transition: 'all var(--homly-transition-fast)',
                    }}
                >
                    <Avatar className="w-10 h-10" style={{ border: '2px solid var(--homly-purple)' }}>
                        <AvatarFallback
                            style={{
                                background: 'var(--homly-gradient-purple)',
                                color: 'white',
                                fontWeight: 700,
                                fontFamily: 'var(--homly-font-display)',
                            }}
                        >
                            {user?.first_name?.[0]}
                            {user?.last_name?.[0]}
                        </AvatarFallback>
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                            style={{
                                margin: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'var(--homly-text-primary)',
                                fontFamily: 'var(--homly-font-display)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {user?.first_name} {user?.last_name}
                        </p>
                        <p
                            style={{
                                margin: '2px 0 0 0',
                                fontSize: 10,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'var(--homly-text-tertiary)',
                                fontFamily: 'var(--homly-font-mono)',
                            }}
                        >
                            {user?.role}
                        </p>
                    </div>
                </NavLink>
            </div>
        </aside>
    );
}

// ─── Lien direct (sans children) ───────────────────────────────────────
function SectionLink({ item, basePath, mandateCount, unreadMessages }) {
    return (
        <NavLink
            to={item.to}
            end={item.exactMatch || item.to === basePath}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 14px',
                marginBottom: 4,
                borderRadius: 'var(--homly-radius-md)',
                fontSize: 13.5,
                fontFamily: 'var(--homly-font-display)',
                fontWeight: 600,
                color: isActive ? 'white' : 'var(--homly-text-secondary)',
                background: isActive ? 'var(--homly-gradient-purple)' : 'transparent',
                boxShadow: isActive ? '0 4px 16px rgba(168, 85, 247, 0.25)' : 'none',
                textDecoration: 'none',
                transition: 'all var(--homly-transition-fast)',
            })}
        >
            {item.icon && <item.icon size={18} strokeWidth={2.2} />}
            <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
            <Badge label={item.label} mandateCount={mandateCount} unreadMessages={unreadMessages} />
        </NavLink>
    );
}

// ─── Section avec sous-menu collapsible ─────────────────────────────────
function SectionGroup({ section, open, onToggle, unreadMessages, mandateCount }) {
    const location = useLocation();
    const hasActiveChild = section.children.some(
        (c) => location.pathname === c.to || location.pathname.startsWith(`${c.to}/`)
    );
    const locked = !!section.locked;

    const handleClick = () => {
        if (locked) return;  // clic bloqué quand verrouillé
        onToggle();
    };

    return (
        <div style={{ marginBottom: 4 }}>
            <button
                type="button"
                onClick={handleClick}
                data-testid={`nav-section-${section.key}`}
                aria-expanded={locked ? false : open}
                aria-disabled={locked}
                title={locked ? section.lockedTooltip : undefined}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '11px 14px',
                    border: 'none',
                    cursor: locked ? 'not-allowed' : 'pointer',
                    borderRadius: 'var(--homly-radius-md)',
                    fontSize: 13.5,
                    fontFamily: 'var(--homly-font-display)',
                    fontWeight: 600,
                    color: locked
                        ? 'var(--homly-text-muted, #6B5A99)'
                        : hasActiveChild ? 'var(--homly-text-primary)' : 'var(--homly-text-secondary)',
                    background: hasActiveChild && !locked
                        ? 'rgba(168, 85, 247, 0.08)'
                        : 'transparent',
                    opacity: locked ? 0.65 : 1,
                    transition: 'all var(--homly-transition-fast)',
                    textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                    if (!hasActiveChild && !locked) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
                onMouseLeave={(e) => {
                    if (!hasActiveChild && !locked) e.currentTarget.style.background = 'transparent';
                }}
            >
                {section.icon && (
                    <section.icon
                        size={18}
                        strokeWidth={2.2}
                        style={{ color: hasActiveChild && !locked ? 'var(--homly-purple)' : 'inherit' }}
                    />
                )}
                <span style={{ flex: 1 }}>{section.label}</span>
                {section.badge === 'mandate-count' && mandateCount > 0 && !locked && (
                    <span style={countBadgeStyle('purple')}>{mandateCount}</span>
                )}
                {locked ? (
                    <Lock size={13} strokeWidth={2.4} style={{ opacity: 0.85, color: '#FF914D' }} data-testid={`nav-lock-${section.key}`} />
                ) : (
                    <ChevronDown
                        size={14}
                        strokeWidth={2.5}
                        style={{
                            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform var(--homly-transition-fast)',
                            opacity: 0.7,
                        }}
                    />
                )}
            </button>

            {/* Children */}
            {open && !locked && (
                <div
                    role="group"
                    style={{
                        marginTop: 2,
                        marginLeft: 14,
                        paddingLeft: 14,
                        borderLeft: '1px solid var(--homly-border-subtle)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {section.children.map((child) => (
                        <NavLink
                            key={child.to}
                            to={child.to}
                            end
                            data-testid={`nav-sub-${child.to.replace(/\//g, '-')}`}
                            style={({ isActive }) => ({
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '8px 12px',
                                borderRadius: 'var(--homly-radius-sm)',
                                fontSize: 12.5,
                                fontFamily: 'var(--homly-font-body)',
                                fontWeight: isActive ? 600 : 500,
                                color: isActive ? 'var(--homly-purple)' : 'var(--homly-text-tertiary)',
                                background: isActive ? 'rgba(168, 85, 247, 0.10)' : 'transparent',
                                textDecoration: 'none',
                                transition: 'all var(--homly-transition-fast)',
                            })}
                            onMouseEnter={(e) => {
                                if (!e.currentTarget.getAttribute('aria-current')) {
                                    e.currentTarget.style.color = 'var(--homly-text-secondary)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!e.currentTarget.getAttribute('aria-current')) {
                                    e.currentTarget.style.color = 'var(--homly-text-tertiary)';
                                }
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    background: 'currentColor',
                                    opacity: 0.6,
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ flex: 1, minWidth: 0 }}>{child.label}</span>
                            {child.badge === 'unread' && unreadMessages > 0 && (
                                <span style={countBadgeStyle('orange')}>{unreadMessages}</span>
                            )}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Badge counts ───────────────────────────────────────────────────────
function Badge({ label, mandateCount, unreadMessages }) {
    if (label === 'Messages' && unreadMessages > 0) {
        return <span style={countBadgeStyle('orange')}>{unreadMessages}</span>;
    }
    return null;
}

function countBadgeStyle(color) {
    const bg = color === 'orange' ? 'var(--homly-gradient-orange)' : 'var(--homly-gradient-purple)';
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 22,
        height: 18,
        padding: '0 6px',
        background: bg,
        borderRadius: 'var(--homly-radius-pill)',
        fontSize: 10,
        fontWeight: 700,
        color: 'white',
        fontFamily: 'var(--homly-font-mono)',
        letterSpacing: 0,
    };
}

export default SidebarDesktop;
